import { useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Plan } from '@cfa/pathway-fe-shared/xp';

import type { AddSortFields } from '@/hooks/useSortOptions';
import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { itemByLanguage } from '@/utils/language';
import { showAssignmentSuccessToast } from '@/utils/training-plans/show-assignment-success-toast';

import ManagedPlansCard from '@/components/Cards/PlanCard/PlanCards/ManagedPlans/ManagedPlansCard';
import ClearFiltersHeader from '@/components/ClearFiltersHeader/ClearFiltersHeader';
import { Label } from '@/components/Filter/components/FilterCheckboxSection';
import NoMessage from '@/components/NoMessage/NoMessage';
import { AssignTeamMembersModal } from '@/components/popups/AssignTeamMembersModal';
import ManageTranslationsModal from '@/components/popups/ManageTranslationsModal/ManageTranslationsModal';
import type { ManageTranslationsState } from '@/components/popups/ManageTranslationsModal/state';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import ArchivePlan from './Modals/ArchivePlan';
import DuplicatePlan from './Modals/DuplicatePlan';
import RemovePlan from './Modals/RemovePlan';

import { useDataView } from '@/atoms/data-view';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { useAddReadOnlyPlan } from '@/routes/TrainingPlans/hooks/useAddReadOnlyPlan/useAddReadOnlyPlan';
import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';
import TrainTogetherModal from '@/routes/TrainingPlans/ManagePlans/Modals/TrainTogetherModal';

interface PlanCardsProps {
  categoryLabels: Label;
  currentFilters: string[];
  filteredAndSortedPlans: Plan[];
  isFetchingPlans: boolean;
  refetchPlans: () => void;
  searchFilter: string;
}

const PlanCards: React.FC<PlanCardsProps> = ({
  categoryLabels,
  currentFilters,
  filteredAndSortedPlans,
  isFetchingPlans,
  refetchPlans,
  searchFilter,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const { updateTranslations } = useUpdatePlan();
  const { assignTeamMembers } = useAssignTeamMembers();
  const [showAddTeamMembersPopup, setShowAddTeamMembersPopup] = useState(false);
  const [editTranslations, setEditTranslations] = useState(false);
  const [showDuplicatePlan, setShowDuplicatePlan] = useState(false);
  const [showArchivePlan, setShowArchivePlan] = useState(false);
  const [showRemovePlan, setShowRemovePlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const [
    {
      display: { total },
    },
    { resetCheckboxByValue, resetAllCheckboxFilters },
  ] = useDataView<AddSortFields<Plan>>('manage-plans');

  const { unsubscribeReadonlyPlan, isPending: isRemovePending } =
    useAddReadOnlyPlan();

  const handleRemove = async () => {
    if (!selectedPlan) return;
    await unsubscribeReadonlyPlan({
      id: selectedPlan.id,
      name: selectedPlan.name,
    });
    setShowRemovePlan(false);
  };

  const onSaveTranslations = async (updated: ManageTranslationsState) => {
    if (!selectedPlan) return;
    await updateTranslations(selectedPlan, {
      name: updated.name,
      description: updated.description,
      sections: updated.content?.sections,
      steps: updated.content?.steps,
    });
    setEditTranslations(false);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${itemByLanguage(updated.name)} ${t(
          'TrainingPlans.translationToastText',
        )} ${
          userLanguage === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
            ? Constants.LANGUAGE_OPTIONS.SPANISH
            : Constants.LANGUAGE_OPTIONS.INGLES
        }`}
      </ToastMessageBlock>
    ));
    refetchPlans();
  };

  const isPlanCompliance =
    !!selectedPlan &&
    (selectedPlan.type.toLocaleLowerCase() === 'compliance' ||
      selectedPlan.type.toLocaleLowerCase() === 'anchor');

  const onAssign = async (userIds: string[], dueDate: string | null) => {
    const planId = selectedPlan?.id;
    if (!selectedPlan || !planId) {
      datadogRum.addError(new Error('No plan found'));
      return;
    }

    try {
      await assignTeamMembers({
        ...(dueDate ? { planId, userIds, dueDate } : { planId, userIds }),
        isFirstAssignment: selectedPlan.assignedUsers === 0,
      });
      showAssignmentSuccessToast(userIds.length, t);
    } catch (err) {
      datadogRum.addError(err);
    }

    navigate(
      { pathname: routes.plans.managePlan({ planId }) },
      { state: { refetch: true } },
    );
  };

  return (
    <>
      <div className="relative max-w-full grow gap-6">
        <div className="flex max-w-full grow flex-col">
          <ClearFiltersHeader
            aliases={categoryLabels}
            clearAllFilters={() => {
              resetAllCheckboxFilters();
            }}
            clearFilter={value => {
              resetCheckboxByValue(value);
            }}
            filters={currentFilters}
          />

          <div className="relative flex max-w-full grow flex-col">
            {!filteredAndSortedPlans.length &&
              !isFetchingPlans &&
              !currentFilters.length &&
              !searchFilter && (
                <NoMessage
                  message={t('TrainingPlans.createPlanOnBuildPlanPage')}
                />
              )}
            {(!!currentFilters.length || !!searchFilter) && !total && (
              <NoMessage message={t('TrainingPlans.noPlansResults')} />
            )}
            {!!filteredAndSortedPlans.length && !isFetchingPlans && (
              <div className="flex flex-col gap-4">
                {filteredAndSortedPlans.map(plan => (
                  <ManagedPlansCard
                    key={plan.id}
                    assignedUsers={plan.assignedUsers}
                    assignPlan={() => {
                      setSelectedPlan(plan);
                      setShowAddTeamMembersPopup(true);
                    }}
                    completedUsers={plan.completedUsers}
                    archivePlan={() => {
                      setSelectedPlan(plan);
                      setShowArchivePlan(true);
                    }}
                    duplicatePlan={() => {
                      setSelectedPlan(plan);
                      setShowDuplicatePlan(true);
                    }}
                    editTranslations={() => {
                      setSelectedPlan(plan);
                      setEditTranslations(true);
                    }}
                    plan={plan}
                    removePlan={() => {
                      setSelectedPlan(plan);
                      setShowRemovePlan(true);
                    }}
                    sendPlanToParent={() => setSelectedPlan(plan)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <AssignTeamMembersModal
        isOpen={showAddTeamMembersPopup}
        onClose={() => setShowAddTeamMembersPopup(false)}
        plan={showAddTeamMembersPopup ? (selectedPlan ?? undefined) : undefined}
        isCompliance={isPlanCompliance}
        reassignEligible
        teamMemberQueryOptions={{ refetchOnMountOrArgChange: true }}
        onAssign={onAssign}
      />
      {selectedPlan && <TrainTogetherModal plan={selectedPlan} />}
      {selectedPlan && (
        <>
          <ManageTranslationsModal
            isOpen={editTranslations}
            onClose={() => setEditTranslations(false)}
            onSave={onSaveTranslations}
            translatableObject={selectedPlan}
          />
          <DuplicatePlan
            plan={selectedPlan}
            refetchPlans={refetchPlans}
            setShowDuplicatePlan={setShowDuplicatePlan}
            showDuplicatePlan={showDuplicatePlan}
          />
          <ArchivePlan
            plan={selectedPlan}
            setShowArchivePlan={setShowArchivePlan}
            showArchivePlan={showArchivePlan}
          />
          <RemovePlan
            isLoading={isRemovePending}
            onRemove={handleRemove}
            plan={selectedPlan}
            setShowRemovePlan={setShowRemovePlan}
            showRemovePlan={showRemovePlan}
          />
        </>
      )}
    </>
  );
};

export default PlanCards;
